/**
 * OnboardingFlow — 2-step guided setup for new tradies.
 *
 * MIGRATION REQUIRED — run this in Supabase SQL editor before deploying:
 *   ALTER TABLE profiles ADD COLUMN IF NOT EXISTS onboarding_completed boolean DEFAULT false;
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import LogoUpload from "../common/LogoUpload";

// ─── Confetti dots (brand colours only) ──────────────────────────────────────
const CONFETTI_DOTS = [
  { left: "8%",  delay: "0s",    color: "var(--primary)" },
  { left: "18%", delay: "0.15s", color: "#fff" },
  { left: "27%", delay: "0.05s", color: "var(--dark)" },
  { left: "36%", delay: "0.25s", color: "var(--primary)" },
  { left: "45%", delay: "0.1s",  color: "#CC6E00" },
  { left: "54%", delay: "0.3s",  color: "#fff" },
  { left: "63%", delay: "0.08s", color: "var(--primary)" },
  { left: "72%", delay: "0.2s",  color: "var(--dark)" },
  { left: "80%", delay: "0.35s", color: "#CC6E00" },
  { left: "88%", delay: "0.12s", color: "var(--primary)" },
  { left: "93%", delay: "0.28s", color: "#fff" },
  { left: "15%", delay: "0.4s",  color: "#CC6E00" },
];

function ConfettiLayer() {
  return (
    <>
      <style>{`
        @keyframes rqp-confetti-fall {
          0%   { transform: translateY(-24px) rotate(0deg);   opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      {CONFETTI_DOTS.map((d, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: d.left,
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: d.color,
            animation: `rqp-confetti-fall 2.6s ${d.delay} ease-in forwards`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      ))}
    </>
  );
}

// ─── Progress dots ────────────────────────────────────────────────────────────
function ProgressDots({ step, total = 3 }) {
  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "32px" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: i <= step ? "var(--primary)" : "var(--border, #e5e7eb)",
            transition: "background 0.3s",
          }}
        />
      ))}
    </div>
  );
}

// ─── Shared large input style ─────────────────────────────────────────────────
const INPUT_STYLE = {
  width: "100%",
  fontSize: "1.1rem",
  padding: "16px 18px",
  borderRadius: "12px",
  border: "2px solid var(--border, #e5e7eb)",
  boxSizing: "border-box",
  outline: "none",
  marginBottom: "20px",
};

const PRIMARY_BTN = {
  width: "100%",
  background: "var(--primary)",
  color: "#fff",
  border: "none",
  borderRadius: "12px",
  padding: "16px 24px",
  fontSize: "1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const SKIP_LINK = {
  background: "none",
  border: "none",
  color: "var(--muted, #6b7280)",
  fontSize: "0.85rem",
  cursor: "pointer",
  textDecoration: "underline",
  display: "block",
  margin: "14px auto 0",
};

// ─── Step 1 ───────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function Step1({ initialName, userId, onNext }) {
  const [name, setName] = useState(initialName || "");
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  async function handleNext() {
    const trimmed = name.trim();
    if (!trimmed) {
      showToast("Please enter your business name.", "error");
      return;
    }
    setSaving(true);
    try {
      if (trimmed !== (initialName || "").trim()) {
        const { error } = await supabase
          .from("profiles")
          .update({ business_name: trimmed })
          .eq("id", userId);
        if (error) throw error;
      }
      onNext({ businessName: trimmed });
    } catch (err) {
      showToast(err.message || "Failed to save. Try again.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: "28px" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: "16px" }}>👋</div>
        <h2 style={{ margin: "0 0 10px", fontSize: "1.6rem", fontWeight: 800, color: "var(--text, #1C1C1E)" }}>
          Let's get you set up
        </h2>
        <p style={{ margin: 0, color: "var(--muted, #6b7280)", fontSize: "0.95rem", lineHeight: 1.6, maxWidth: "360px", marginLeft: "auto", marginRight: "auto" }}>
          Takes 2 minutes. We'll set up your profile so customers can find you and request quotes.
        </p>
      </div>

      <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "0.95rem" }}>
        What's your business name?
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleNext()}
        placeholder="e.g. Smith Electrical"
        style={INPUT_STYLE}
        autoFocus
      />

      <button type="button" onClick={handleNext} disabled={saving} style={{ ...PRIMARY_BTN, opacity: saving ? 0.7 : 1 }}>
        {saving ? "Saving…" : "Next →"}
      </button>
    </div>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────
function Step2({ userId, initialLogoUrl, onNext, onSkip }) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl || "");
  const [uploading, setUploading] = useState(false);

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: "28px" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: "16px" }}>🎨</div>
        <h2 style={{ margin: "0 0 10px", fontSize: "1.6rem", fontWeight: 800, color: "var(--text, #1C1C1E)" }}>
          Add your business logo
        </h2>
        <p style={{ margin: 0, color: "var(--muted, #6b7280)", fontSize: "0.95rem", lineHeight: 1.6, maxWidth: "360px", marginLeft: "auto", marginRight: "auto" }}>
          Your logo appears on every PDF quote you send. Looks way more professional than no logo.
        </p>
      </div>

      {/* Logo upload area */}
      <div style={{ marginBottom: "28px" }}>
        <LogoUpload
          userId={userId}
          logoUrl={logoUrl}
          onUpload={(url) => setLogoUrl(url)}
          onUploadingChange={setUploading}
          size={110}
          layout="column"
          showSuccessState
        />
      </div>

      <button
        type="button"
        onClick={() => onNext({ logoUrl })}
        disabled={uploading}
        style={{ ...PRIMARY_BTN, opacity: uploading ? 0.7 : 1 }}
      >
        Next →
      </button>
      <button type="button" onClick={onSkip} style={SKIP_LINK}>
        Skip for now →
      </button>
    </div>
  );
}

// ─── Step 3 ───────────────────────────────────────────────────────────────────
function Step3({ businessId, onComplete }) {
  const { showToast } = useToast();
  const link = `${window.location.origin}${window.location.pathname}?tradie=${businessId}`;

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Request a quote from us", text: "Tap to request a quote — fill in your job details and I'll get back to you.", url: link });
      } catch {
        // user cancelled — no toast needed
      }
    } else {
      try {
        await navigator.clipboard.writeText(link);
        showToast("Link copied — paste it into a message!", "success");
      } catch {
        showToast("Copy failed — try manually selecting the link.", "error");
      }
    }
  }

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <ConfettiLayer />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ fontSize: "3.5rem", marginBottom: "16px" }}>🎉</div>
          <h2 style={{ margin: "0 0 10px", fontSize: "1.6rem", fontWeight: 800, color: "var(--text, #1C1C1E)" }}>
            You're ready to go!
          </h2>
          <p style={{ margin: 0, color: "var(--muted, #6b7280)", fontSize: "0.95rem", lineHeight: 1.6 }}>
            Here's the most powerful thing you can do right now:
          </p>
        </div>

        {/* Big action box */}
        <div
          style={{
            background: "var(--dark)",
            border: "2px solid #FF8A00",
            borderRadius: "16px",
            padding: "24px 22px",
            marginBottom: "20px",
          }}
        >
          <div style={{ fontSize: "1.6rem", marginBottom: "10px" }}>📲</div>
          <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff", marginBottom: "8px" }}>
            Send your quote link to a customer
          </div>
          <p style={{ margin: "0 0 18px", fontSize: "0.875rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.6 }}>
            Share it via WhatsApp or SMS and they'll fill in their job details. It goes straight to your pipeline — free, no credits needed.
          </p>
          <button
            type="button"
            onClick={handleShare}
            style={{ ...PRIMARY_BTN, fontSize: "0.95rem" }}
          >
            Share my link now →
          </button>
        </div>

        {/* Secondary CTA */}
        <button
          type="button"
          onClick={onComplete}
          style={{
            width: "100%",
            background: "none",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: "12px",
            padding: "14px 24px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            color: "var(--text, #1C1C1E)",
          }}
        >
          Or explore your dashboard →
        </button>
      </div>
    </div>
  );
}

// ─── OnboardingFlow ───────────────────────────────────────────────────────────
export default function OnboardingFlow({ profile, session, businessId, onComplete }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const { showToast } = useToast();

  const userId = session?.user?.id;

  async function completeOnboarding() {
    if (userId) {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", userId);

      if (error) {
        showToast("Something went wrong — please try again.", "error");
        return; // Keep the modal open so the user can retry.
      }
    }
    showToast("Welcome to Remote Quote! 🎉", "success");
    onComplete();
  }

  function advance() {
    setDirection(1);
    setStep((s) => s + 1);
  }

  function skipToEnd() {
    setDirection(1);
    setStep(1);
  }

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 32 : -32 }),
    center: { opacity: 1, x: 0 },
    exit:  (dir) => ({ opacity: 0, x: dir > 0 ? -32 : 32 }),
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "var(--bg, #f9fafb)",
        overflowY: "auto",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "32px 16px 64px",
      }}
    >
      <div style={{ width: "100%", maxWidth: "480px" }}>
        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px" }}>
          <div style={{ fontSize: "0.8rem", color: "var(--muted, #6b7280)", fontWeight: 600 }}>
            Step {step + 1} of 2
          </div>
          {step < 1 && (
            <button type="button" onClick={skipToEnd} style={{ background: "none", border: "none", color: "var(--muted, #6b7280)", fontSize: "0.85rem", cursor: "pointer", textDecoration: "underline" }}>
              Skip setup →
            </button>
          )}
        </div>

        <ProgressDots step={step} total={2} />

        {/* Step content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22, ease: "easeInOut" }}
          >
            {step === 0 && (
              <Step2
                userId={userId}
                initialLogoUrl={profile?.logo_url || ""}
                onNext={() => advance()}
                onSkip={() => advance()}
              />
            )}
            {step === 1 && (
              <Step3
                businessId={businessId}
                onComplete={completeOnboarding}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
